<script setup lang="ts">
import device from '@@/bits/device'
import { getEmojiKey, OTHER_EMOJIS_CATEGORY } from '@@/bits/emoji'
import OzVirtualList from '@@/library/v4/components/OzVirtualList.vue'
import EmojiListItem from '@@/vuecomponents/EmojiListItem.vue'
import type { Emoji, EmojiCategory } from '@padlet/arvo'
import type { VirtualItem } from '@tanstack/vue-virtual'
import { debounce } from 'es-toolkit'
import { computed, readonly, ref, shallowRef } from 'vue'

const SKELETON_ROWS = [{ type: 'category-header' }, { type: 'emoji-row' }, { type: 'emoji-row' }]

const props = withDefaults(
  defineProps<{
    isLoading: boolean
    emojis: Emoji[]
    darkMode: boolean
    columns?: number
    selectedEmojiUrl?: string
    iconSize?: number
    hoveredIconSize?: number
    checkmarkSize?: number
    groupByCategory?: boolean
    xThinScrollbar?: boolean
    draggable?: boolean
    xCheckmark?: boolean
  }>(),
  {
    columns: DEFAULT_COLUMNS,
    selectedEmojiUrl: '',
    iconSize: 40,
    hoveredIconSize: undefined,
    checkmarkSize: 14,
    groupByCategory: false,
    xThinScrollbar: false,
    draggable: false,
    xCheckmark: true,
  },
)

const emit = defineEmits<{
  (e: 'pick', url: string): void
  (e: 'category-visible', categoryLabel: string): void
  (e: 'emoji-hover', event: MouseEvent | FocusEvent, emoji: Emoji): void
  (e: 'emoji-unhover'): void
}>()

// #region Emoji data

// Groups emojis by their category for display purposes
// Uses category.label as the key to ensure uniqueness (since category objects may not be reference-equal)
// Emojis without a category are grouped under "Other" and placed at the end
const groupedEmojis = computed(() => {
  if (!props.groupByCategory) return []

  // Map using category label as key to avoid object comparison issues
  const groups = new Map<string, { category: EmojiCategory; emojis: Emoji[] }>()

  props.emojis.forEach((emoji) => {
    const category = emoji.category ?? OTHER_EMOJIS_CATEGORY
    const key = category.label

    if (!groups.has(key)) {
      groups.set(key, { category, emojis: [] })
    }
    groups.get(key)!.emojis.push(emoji)
  })

  const result: { category: EmojiCategory; emojis: Emoji[] }[] = []
  let otherGroup: { category: EmojiCategory; emojis: Emoji[] } | null = null

  groups.forEach((group) => {
    if (group.category.label === OTHER_EMOJIS_CATEGORY.label) {
      otherGroup = group
    } else {
      result.push(group)
    }
  })

  // Add 'Other' group at the end if it exists
  if (otherGroup != null) {
    result.push(otherGroup)
  }

  return result
})
// #endregion

// #region Data for virtualization
const safeColumns = computed(() => Math.max(1, Math.floor(props.columns || DEFAULT_COLUMNS)))

interface VirtualRowEmojis {
  type: 'emojis'
  data: Emoji[]
  rowIndex: number
  /** Index of emoji in the current row */
  startIndex: number
  /** Index of emoji in the flat array (`props.emoji`) */
  flatStartIndex: number
}

interface VirtualRowCategoryHeader {
  type: 'category-header'
  data: EmojiCategory
  rowIndex: number
}

type VirtualRow = VirtualRowEmojis | VirtualRowCategoryHeader

/**
 * Convert flat emoji array to rows based on the number of columns.
 */
const convertEmojisToRows = (emojis: Emoji[], columns: number, flatStartIndex: number = 0): VirtualRow[] => {
  const rows: VirtualRow[] = []
  for (let i = 0; i < emojis.length; i += columns) {
    rows.push({
      type: 'emojis',
      data: emojis.slice(i, i + columns),
      rowIndex: rows.length,
      startIndex: i,
      flatStartIndex: flatStartIndex + i,
    })
  }
  return rows
}

/**
 * Converts grouped emojis into rows with category headers.
 */
const convertGroupedEmojisToRows = (
  groups: Array<{ category: EmojiCategory; emojis: Emoji[] }>,
  columns: number,
): VirtualRow[] => {
  const rows: VirtualRow[] = []
  let flatIndex = 0 // Track flat array position across all groups

  groups.forEach((group) => {
    // Add category header row
    rows.push({
      type: 'category-header',
      data: group.category,
      rowIndex: rows.length,
    })

    // Add emoji rows for this category
    for (let i = 0; i < group.emojis.length; i += columns) {
      rows.push({
        type: 'emojis',
        data: group.emojis.slice(i, i + columns),
        rowIndex: rows.length,
        startIndex: i,
        flatStartIndex: flatIndex + i,
      })
    }

    // Move to next category's flat index
    flatIndex += group.emojis.length
  })

  return rows
}

// Convert emojis to virtual rows based on rendering mode
const virtualRowsData = computed<VirtualRow[]>(() => {
  if (props.groupByCategory) {
    return convertGroupedEmojisToRows(groupedEmojis.value, safeColumns.value)
  } else {
    return convertEmojisToRows(props.emojis, safeColumns.value, 0)
  }
})
// #endregion

// #region Virtualization setup

/**
 * Calculates the height of a row based on its type.
 * Also includes gap because we have 2 types of rows
 * and they don't have uniform gap (so we can't use the
 * `itemGap` prop of `OzVirtualList`).
 */
const getRowHeight = (row: VirtualRow, iconSize: number): number => {
  const EMOJI_PADDING = 4 // padding around each emoji
  const EMOJI_ROW_GAP = 12
  const CATEGORY_HEADER_HEIGHT = 33

  if (row.type === 'category-header') {
    return CATEGORY_HEADER_HEIGHT
  }
  return iconSize + EMOJI_PADDING * 2 + EMOJI_ROW_GAP
}

const estimateItemSize = (index: number): number => {
  return getRowHeight(virtualRowsData.value[index], props.iconSize)
}

const virtualList = shallowRef<InstanceType<typeof OzVirtualList>>()
// #endregion

// #region Track scroll position and detect visible category
const findCategoryRowIndex = (categoryLabel: string): number | undefined => {
  const index = virtualRowsData.value.findIndex(
    (row) => row.type === 'category-header' && row.data.label === categoryLabel,
  )
  return index >= 0 ? index : undefined
}

// Track the currently visible category
const visibleCategoryLabel = ref<string | null>(null)

// Track the currently visible emoji index in the flat emoji array
const visibleEmojiIndex = ref<number>(0)

/**
 * Finds the virtual row index that contains the specified flat emoji array index
 */
const findVirtualRowFromEmojiIndex = (emojiIndex: number): number | undefined => {
  if (emojiIndex < 0 || emojiIndex >= props.emojis.length) {
    return undefined
  }

  // Simple O(n) lookup through virtual rows to find matching flatStartIndex
  for (let i = 0; i < virtualRowsData.value.length; i++) {
    const row = virtualRowsData.value[i]
    if (row.type === 'emojis') {
      const rowEndIndex = row.flatStartIndex + row.data.length - 1
      if (emojiIndex >= row.flatStartIndex && emojiIndex <= rowEndIndex) {
        return i
      }
    }
  }

  return undefined
}

/**
 * Detects which category and emoji index is currently visible based on scroll position
 */
const detectVisibleCategory = (): void => {
  if (!props.groupByCategory) return

  const scrollTop = virtualList.value?.scrollOffset ?? 0
  const visibleItems = virtualList.value?.virtualItems ?? []

  if (visibleItems.length === 0) return

  // Find the first emoji row that is meaningfully visible (not just barely at top)
  let firstEmojiItem: { item: VirtualItem; row: VirtualRowEmojis } | null = null
  for (const item of visibleItems) {
    const row = virtualRowsData.value[item.index]
    if (row != null && row.type === 'emojis') {
      // Only consider rows where at least half the row is visible past the scroll top
      const rowHeight = item.size
      const visibleHeight = item.start + rowHeight - scrollTop
      const visibilityRatio = visibleHeight / rowHeight

      if (visibilityRatio > 0.5) {
        // At least 50% of the row is visible
        firstEmojiItem = { item, row }
        break
      }
    }
  }

  // Update visible emoji index if we found a visible emoji row
  if (firstEmojiItem) {
    const newEmojiIndex = firstEmojiItem.row.flatStartIndex
    if (newEmojiIndex >= 0 && newEmojiIndex !== visibleEmojiIndex.value) {
      visibleEmojiIndex.value = newEmojiIndex
    }
  }

  // Find the last category header that is at or above the top of the viewport
  let lastVisibleCategory: string | null = null

  // Check all items to find the most relevant category
  for (const item of visibleItems) {
    const row = virtualRowsData.value[item.index]
    if (row == null) continue

    if (row.type === 'category-header') {
      // Check if this category header is at or above the top of the viewport (with 10px threshold)
      if (item.start <= scrollTop + 10) {
        lastVisibleCategory = row.data.label
      }
    }
  }

  // If we found a visible category header, use it
  if (lastVisibleCategory) {
    if (visibleCategoryLabel.value !== lastVisibleCategory) {
      visibleCategoryLabel.value = lastVisibleCategory
      emit('category-visible', lastVisibleCategory)
    }
    return
  }

  // Otherwise, find the category of the first visible item by walking backwards
  const firstVisibleItem = visibleItems[0]
  if (firstVisibleItem != null && firstVisibleItem.index >= 0) {
    for (let i = firstVisibleItem.index; i >= 0; i--) {
      const row = virtualRowsData.value[i]
      if (row == null) continue

      if (row.type === 'category-header') {
        if (visibleCategoryLabel.value !== row.data.label) {
          visibleCategoryLabel.value = row.data.label
          emit('category-visible', row.data.label)
        }
        return
      }
    }
  }
}

const debouncedDetectVisibleCategory = debounce(detectVisibleCategory, 100)

const handleScroll = (newOffset: number): void => {
  if (newOffset != null && props.groupByCategory) {
    debouncedDetectVisibleCategory()
  }
}
// #endregion

// #region Hover styling
const effectiveHoveredIconSize = computed(() => {
  // hoveredIconSize has a hard-coded default value but if only iconSize is provided,
  // we want to use that instead.
  return props.hoveredIconSize ?? props.iconSize
})
const hoverScaleFactor = computed(() => effectiveHoveredIconSize.value / props.iconSize)
// #endregion

const isScrolled = computed<boolean>(() => {
  return (virtualList.value?.scrollOffset ?? 0) > 0
})

const scrollToCategory = (categoryLabel: string) => {
  if (!props.groupByCategory) return

  const rowIndex = findCategoryRowIndex(categoryLabel)
  if (rowIndex !== undefined) {
    virtualList.value?.scrollToIndex(rowIndex, {
      align: 'start',
      behavior: 'auto',
    })
  }
}

/**
 * Scrolls to the specified emoji by its flat array index
 */
const scrollToEmoji = (emojiIndex: number) => {
  const rowIndex = findVirtualRowFromEmojiIndex(emojiIndex)
  if (rowIndex !== undefined) {
    virtualList.value?.scrollToIndex(rowIndex, {
      align: 'start',
      behavior: 'auto',
    })
  }
}

defineExpose({
  visibleEmojiIndex: readonly(visibleEmojiIndex),
  isScrolled,
  scrollToCategory,
  scrollToEmoji,
})
</script>

<script lang="ts">
export const DEFAULT_COLUMNS = 6
</script>

<template>
  <!-- Loading state -->
  <div v-if="isLoading">
    <template v-for="(row, rIndex) in SKELETON_ROWS" :key="`category-header-${rIndex}`">
      <div
        v-if="row.type === 'category-header'"
        :class="[
          'rounded-xl',
          'box-border',
          'w-17.5 h-2.75',
          'mx-2 first:my-2.75 mt-5.75 mb-2.75',
          {
            'bg-color-change-light motion-reduce:bg-button-hover-light': !darkMode,
            'bg-color-change-dark motion-reduce:bg-button-hover-dark': darkMode,
          },
        ]"
      />

      <div
        v-else
        :key="`emoji-row-${rIndex}`"
        class="grid mb-3"
        :style="{
          gridTemplateColumns: `repeat(${safeColumns}, 1fr)`,
        }"
      >
        <div v-for="(_, cIndex) in safeColumns" :key="`cell-${cIndex}`" class="flex items-center justify-center">
          <div
            :class="[
              'rounded-xl',
              {
                'bg-color-change-light motion-reduce:bg-button-hover-light': !darkMode,
                'bg-color-change-dark motion-reduce:bg-button-hover-dark': darkMode,
              },
            ]"
            :style="{
              width: `${iconSize}px`,
              height: `${iconSize}px`,
            }"
          />
        </div>
      </div>
    </template>
  </div>

  <!-- Grid Content -->
  <OzVirtualList
    v-else
    ref="virtualList"
    :direction="'vertical'"
    :items="virtualRowsData"
    :estimate-item-size="estimateItemSize"
    :class="[
      'relative',
      {
        'scrollbar-photo': !darkMode && !xThinScrollbar,
        'scrollbar-photo-dark': darkMode && !xThinScrollbar,
        'scrollbar-thin': !darkMode && xThinScrollbar,
        'scrollbar-thin-dark': darkMode && xThinScrollbar,
      },
    ]"
    @scroll="handleScroll"
  >
    <template #item="{ data: row }">
      <!-- Category header row -->
      <p
        v-if="row.type === 'category-header'"
        :class="[
          'p-2',
          {
            'text-dark-text-200': !darkMode,
            'text-light-text-200': darkMode,
          },
          'text-body-extra-small',
        ]"
      >
        {{ row.data.title }}
      </p>

      <!-- Emoji row -->
      <div
        v-else
        class="grid"
        :style="{
          gridTemplateColumns: `repeat(${safeColumns}, 1fr)`,
          '--hover-scale-factor': hoverScaleFactor,
        }"
      >
        <div
          v-for="(emoji, colIndex) in row.data"
          :key="`${getEmojiKey(emoji)}-${colIndex}`"
          class="flex items-center justify-center"
        >
          <EmojiListItem
            :data-key="getEmojiKey(emoji)"
            :emoji="emoji"
            :dark-mode="darkMode"
            :is-selected="selectedEmojiUrl === emoji.url"
            :icon-size="iconSize"
            :hovered-icon-size="hoveredIconSize"
            :checkmark-size="checkmarkSize"
            :draggable="draggable"
            :x-checkmark="xCheckmark"
            class="w-min"
            @mouseenter="$emit('emoji-hover', $event, emoji)"
            @mouseleave="$emit('emoji-unhover')"
            @focus="$emit('emoji-hover', $event, emoji)"
            @blur="$emit('emoji-unhover')"
            @pick="$emit('pick', emoji.url)"
          />
        </div>
      </div>
    </template>
  </OzVirtualList>
</template>

<style scoped>
@keyframes colorChangeLight {
  0%,
  100% {
    @apply bg-button-hover-light;
  }
  50% {
    @apply bg-button-emoji-other-light;
  }
}

@keyframes colorChangeDark {
  0%,
  100% {
    @apply bg-button-hover-dark;
  }
  50% {
    @apply bg-button-emoji-hover-dark;
  }
}

.bg-color-change-light {
  @apply transition-colors duration-500;
  animation: colorChangeLight 4s infinite;
}

.bg-color-change-dark {
  @apply transition-colors duration-500;
  animation: colorChangeDark 4s infinite;
}
</style>
